import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Search } from 'lucide-react';

// Zajednički dijelovi za stranice Grupe, Korisnici i Kategorije
// (isti jezik kao Zadaci / Obavijesti: bijela kartica s razdjelnicima, donji panel, FAB).

export const NAVY = '#1a2a4a';
export const HUE = { pending: '#5b6472', prog: '#3a6ea3', done: '#3f8f6b', bad: '#ac463b', amber: '#a8781f', violet: '#6c5ce7' };

const AV_COLORS = ['#5b7fb0', '#8a6fae', '#4f9d84', '#b0693f', '#3a6ea3', '#a8781f', '#6c5ce7'];
export const avatarColor = (s) => AV_COLORS[[...(s || '?')].reduce((h, c) => h + c.charCodeAt(0), 0) % AV_COLORS.length];
export const initialsOf = (n) => (n || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();

export function plural(n, one, few, many) {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

export function Avatar({ name, size = 36 }) {
  return (
    <span className="adm-av" style={{ width: size, height: size, fontSize: size * 0.36, background: avatarColor(name) }}>
      {initialsOf(name)}
    </span>
  );
}

export function AvatarStack({ names, max = 4 }) {
  return (
    <span className="adm-stack">
      {names.slice(0, max).map(n => <Avatar key={n} name={n} size={24} />)}
    </span>
  );
}

export function useIsMobile() {
  const [m, setM] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const on = () => setM(window.innerWidth <= 768);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return m;
}

// Donji panel (mobitel) / centrirani prozor (desktop). Zatvara se povlačenjem zaglavlja,
// dodirom izvan panela, X-om ili Escapeom. Pozadina se ne skrola dok je otvoren.
// dirty: ima nespremljenih promjena → svaki pokušaj zatvaranja vrati panel i pita „Odbaciti promjene?”.
// busy: sprema se → zatvaranje se ignorira. closeRef: roditelj dobiva istu provjeru (npr. gumb Odustani).
// swipeBody: na mobitelu se panel zatvara i povlačenjem sadržaja prema dolje kad je sadržaj na vrhu (kao detalj zadatka).
export function Sheet({ onClose, head, children, footer, tall, z, dirty = false, busy = false, closeRef, discardText, bare = false, swipeBody = false }) {
  const isMobile = useIsMobile();
  const [entered, setEntered] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const drag = useRef({ y: 0, active: false, cap: false, id: 0, el: null });
  const overlayRef = useRef(null);
  const bodyRef = useRef(null);
  const [ask, setAsk] = useState(false);
  const requestClose = () => {
    setDragY(0);
    if (busy) return;
    if (dirty) setAsk(true);
    else onClose();
  };
  const onCloseRef = useRef(requestClose);
  useEffect(() => { onCloseRef.current = requestClose; if (closeRef) closeRef.current = requestClose; });

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const id = requestAnimationFrame(() => setEntered(true));
    // Esc zatvara samo gornji panel (npr. „Dodaj ljude” iznad „Novi zadatak”).
    const onKey = (e) => { if (e.key === 'Escape' && [...document.querySelectorAll('.adm-overlay')].pop() === overlayRef.current) onCloseRef.current(); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; cancelAnimationFrame(id); window.removeEventListener('keydown', onKey); };
  }, []);

  const onDown = (e) => {
    if (!isMobile || e.target.closest('button, input, textarea, select')) return;
    drag.current = { y: e.clientY, active: true, cap: false, id: e.pointerId, el: e.currentTarget };
  };
  const onMove = (e) => {
    const d = drag.current;
    if (!d.active) return;
    const dy = e.clientY - d.y;
    if (!d.cap) {
      if (dy <= 6) return;
      d.cap = true;
      setDragging(true);
      try { d.el.setPointerCapture(d.id); } catch { /* ignore */ }
    }
    setDragY(Math.max(0, dy));
  };
  const onUp = (e) => {
    const d = drag.current;
    if (!d.active) return;
    d.active = false;
    if (d.cap) { try { d.el.releasePointerCapture(d.id); } catch { /* ignore */ } }
    setDragging(false);
    if (e.clientY - d.y > 100) requestClose();
    else setDragY(0);
  };

  // Povlačenje sadržaja (touch): kreće tek kad je sadržaj na vrhu i prst ide prema dolje; ne iz polja za unos.
  useEffect(() => {
    const el = bodyRef.current;
    if (!swipeBody || !isMobile || !el) return;
    let startY = 0, active = false, skip = false;
    const onStart = (e) => { startY = e.touches[0].clientY; active = false; skip = !!e.target.closest('input, textarea, select'); };
    const onMove = (e) => {
      if (skip) return;
      const dy = e.touches[0].clientY - startY;
      if (!active) {
        if (el.scrollTop <= 0 && dy > 4) { active = true; setDragging(true); }
        else return;
      }
      e.preventDefault();
      setDragY(Math.max(0, dy));
    };
    const onEnd = (e) => {
      if (!active) return;
      active = false;
      setDragging(false);
      const dy = (e.changedTouches && e.changedTouches[0] ? e.changedTouches[0].clientY : startY) - startY;
      if (dy > 100) onCloseRef.current();
      else setDragY(0);
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [swipeBody, isMobile]);

  return (
    <div ref={overlayRef} className={`adm-overlay${isMobile ? ' is-mobile' : ''}`} style={{ opacity: entered ? 1 : 0, zIndex: z }}
      onClick={e => e.target === e.currentTarget && requestClose()}>
      <div className={`adm-sheet${tall ? ' is-tall' : ''}`}
        style={isMobile
          ? { transform: `translateY(${entered ? dragY + 'px' : '100%'})`, transition: dragging ? 'none' : undefined }
          : { opacity: entered ? 1 : 0, transform: entered ? 'none' : 'translateY(12px)' }}>
        <div className="adm-sheet-head" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
          {isMobile && <div className="adm-grab" />}
          <div className="adm-sheet-headrow">
            <div className="adm-sheet-headmain">{head}</div>
            <button type="button" className="adm-x" onClick={requestClose} title="Zatvori"><X size={20} /></button>
          </div>
        </div>
        <div ref={bodyRef} className={`adm-sheet-body${bare ? ' is-bare' : ''}`}>{children}</div>
        {footer && <div className="adm-sheet-foot">{footer}</div>}
        {ask && (
          <div className="adm-ask-scrim" onClick={() => setAsk(false)}>
            <div className="adm-ask" role="alertdialog" aria-modal="true" onClick={e => e.stopPropagation()}>
              <b>Odbaciti promjene?</b>
              <p>{discardText || 'Promjene koje si napravio/la nisu spremljene i bit će izgubljene.'}</p>
              <div className="adm-two">
                <button type="button" className="adm-btn" onClick={() => setAsk(false)}>Nastavi uređivanje</button>
                <button type="button" className="adm-btn red" onClick={() => { setAsk(false); onClose(); }}>Odbaci</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function Fab({ onClick, title }) {
  return (
    <button type="button" className="adm-fab" onClick={onClick} title={title} aria-label={title}>
      <Plus size={24} />
    </button>
  );
}

export function SearchBox({ value, onChange, placeholder }) {
  return (
    <label className="adm-search">
      <Search size={17} />
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
      {value && <button type="button" onClick={() => onChange('')} title="Očisti"><X size={16} /></button>}
    </label>
  );
}

export function Seg({ options, value, onChange, disabled }) {
  return (
    <div className="adm-seg" style={{ gridTemplateColumns: `repeat(${options.length}, 1fr)` }}>
      {options.map(o => (
        <button key={o.value} type="button" disabled={disabled} className={value === o.value ? 'on' : ''} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

// Potvrda brisanja u samom panelu (umjesto window.confirm).
export function ConfirmBox({ title, text, confirmLabel = 'Obriši', onCancel, onConfirm, busy }) {
  return (
    <div className="adm-confirm">
      <b>{title}</b>
      {text && <p>{text}</p>}
      <div className="adm-two">
        <button type="button" className="adm-btn" onClick={onCancel}>Odustani</button>
        <button type="button" className="adm-btn red" onClick={onConfirm} disabled={busy}>{confirmLabel}</button>
      </div>
    </div>
  );
}
